import { createContext, useContext, type RefObject } from 'react';

/**
 * The app shell's scroll container. Since the sidebar prototype pinned the
 * chrome (header / sidebar / breadcrumb), the WINDOW no longer scrolls —
 * the content column does. Anything that used `window.scrollY` /
 * `window.scrollTo` (ScrollToTopButton, the wizard's section-change
 * scroll) must target this container instead. Null outside the shell
 * (auth pages, tests) — consumers fall back to the window.
 */
export const ScrollContainerContext =
  createContext<RefObject<HTMLDivElement | null> | null>(null);

export function useScrollContainer(): RefObject<HTMLDivElement | null> | null {
  return useContext(ScrollContainerContext);
}

/** Smooth-scroll the shell container (or the window, outside the shell)
 *  back to the top. jsdom-safe: falls back to a scrollTop assignment when
 *  Element.scrollTo isn't implemented. */
export function useScrollToTop(): () => void {
  const ref = useScrollContainer();
  return () => {
    const el = ref?.current;
    if (el) {
      if (typeof el.scrollTo === 'function') el.scrollTo({ top: 0, behavior: 'smooth' });
      else el.scrollTop = 0;
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
}
